import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { ProvidersService } from '../../../services/ProvidersService';

const DescriptionStep = ({ provider, setProvider, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    shortDescription: provider?.shortDescription || '',
    businessSummary: provider?.businessSummary || ''
  });

  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');

  // Sync formData from provider when provider changes (e.g., after generation)
  useEffect(() => {
    setFormData({
      shortDescription: provider?.shortDescription || '',
      businessSummary: provider?.businessSummary || ''
    });
  }, [provider]);

  // Generate descriptions automatically on mount if needed
  useEffect(() => {
    const shouldGenerate =
      provider?.id &&
      !formData.shortDescription?.trim() &&
      !formData.businessSummary?.trim();

    if (shouldGenerate) {
      generate();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setGenError('');
    setGenerating(true);
    try {
      const updated = await ProvidersService.generateDescriptions(provider.id);
      setProvider(updated);
    } catch (e) {
      setGenError("We couldn't generate descriptions automatically. You can write them yourself or try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleChange = (evt) => {
    const { name, value } = evt.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSaveAndContinue = () => {
    onNext({
      ...provider,
      shortDescription: formData.shortDescription,
      businessSummary: formData.businessSummary
    });
  };

  return (
    <Box
      display="grid"
      gap="10px"
      sx={{ maxWidth: '600px', margin: '0 auto' }}
    >
      <Typography variant="h4" sx={{ marginBottom: '20px' }}>
        Business Description
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: '20px' }}>
        We generated these from your business details and services. Review and edit them, then continue.
      </Typography>

      {generating && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <CircularProgress size={24} />
          <Typography variant="body2">
            Generating your descriptions…
          </Typography>
        </Box>
      )}

      <TextField
        margin="normal"
        label="Short Description"
        name="shortDescription"
        onChange={handleChange}
        value={formData.shortDescription}
        fullWidth
        multiline
        minRows={2}
        disabled={generating}
      />

      <TextField
        margin="normal"
        label="Business Summary"
        name="businessSummary"
        onChange={handleChange}
        value={formData.businessSummary}
        fullWidth
        multiline
        minRows={4}
        disabled={generating}
      />

      <Box sx={{ marginTop: '10px' }}>
        <Button
          variant="outlined"
          onClick={generate}
          disabled={generating}
          fullWidth
        >
          Regenerate
        </Button>
      </Box>

      {genError && (
        <Alert severity="warning" sx={{ marginTop: '20px' }}>
          {genError}
        </Alert>
      )}

      {/* Bottom Navigation */}
      <Box sx={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button variant="contained" onClick={handleSaveAndContinue} disabled={generating}>
          Save & Continue
        </Button>
      </Box>
    </Box>
  );
};

DescriptionStep.propTypes = {
  provider: PropTypes.object.isRequired,
  setProvider: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired
};

export default DescriptionStep;
